// src/pages/AiPlatform/index.js — "IA da plataforma" (super): chave, preços, consumo por empresa, fechamento
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";

import MainContainer from "../../components/MainContainer";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { PageHeader, Section, StatCard, StatusBadge } from "../../components/ui";
import {
  usePanelStyles,
  FormField,
  FormGrid,
} from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { brl } from "../../components/AiUsageCard";

import useStyles from "./styles";

const PRICE_COLUMNS = [
  { key: "inputPerMillionUsd", label: "inputPerMillion" },
  { key: "outputPerMillionUsd", label: "outputPerMillion" },
  { key: "audioPerMinuteUsd", label: "audioPerMinute" },
  { key: "ttsPerMillionCharsUsd", label: "ttsPerMillionChars" },
];

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const toNumber = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : fallback;
};

const fmt = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ---------------- Configurações ---------------- */

const SettingsSection = () => {
  const classes = useStyles();
  const [hasKey, setHasKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [usdBrl, setUsdBrl] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [quotaMessage, setQuotaMessage] = useState("");
  const [visionModel, setVisionModel] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setHasKey(!!data?.hasKey);
    setUsdBrl(data?.usdBrl === undefined || data?.usdBrl === null ? "" : String(data.usdBrl));
    setMultiplier(data?.multiplier === undefined || data?.multiplier === null ? "" : String(data.multiplier));
    setQuotaMessage(data?.quotaMessage || "");
    setVisionModel(data?.visionModel || "");
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/ai-usage/settings");
        if (active) apply(data);
      } catch (err) {
        toastError(err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { quotaMessage };
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      if (usdBrl !== "") payload.usdBrl = toNumber(usdBrl);
      if (multiplier !== "") payload.multiplier = toNumber(multiplier);
      if (visionModel.trim()) payload.visionModel = visionModel.trim();
      const { data } = await api.put("/ai-usage/settings", payload);
      apply(data);
      setApiKey("");
      toast.success(i18n.t("aiUsage.platform.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      className={classes.block}
      title={i18n.t("aiUsage.platform.settings")}
      actions={
        hasKey && (
          <StatusBadge
            color="success"
            icon={<LockOutlinedIcon style={{ fontSize: 13 }} />}
            label={i18n.t("aiUsage.platform.keySet")}
          />
        )
      }
    >
      <FormGrid>
        <FormField label={i18n.t("aiUsage.platform.key")} htmlFor="ai-platform-key">
          <TextField
            id="ai-platform-key"
            fullWidth
            size="small"
            variant="outlined"
            type="password"
            autoComplete="new-password"
            placeholder={i18n.t("aiUsage.platform.keyPlaceholder")}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <VpnKeyOutlinedIcon className={classes.adornIcon} />
                </InputAdornment>
              ),
            }}
          />
        </FormField>
        <FormGrid>
          <FormField label={i18n.t("aiUsage.platform.usdBrl")} htmlFor="ai-platform-usdbrl">
            <TextField
              id="ai-platform-usdbrl"
              fullWidth
              size="small"
              variant="outlined"
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              value={usdBrl}
              onChange={(e) => setUsdBrl(e.target.value)}
            />
          </FormField>
          <FormField label={i18n.t("aiUsage.platform.multiplier")} htmlFor="ai-platform-multiplier">
            <TextField
              id="ai-platform-multiplier"
              fullWidth
              size="small"
              variant="outlined"
              type="number"
              inputProps={{ step: "0.1", min: 0 }}
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value)}
            />
          </FormField>
        </FormGrid>
        <FormField
          label={i18n.t("aiUsage.platform.visionModel")}
          help={i18n.t("aiUsage.platform.visionModelHelp")}
          htmlFor="ai-platform-vision"
        >
          <TextField
            id="ai-platform-vision"
            fullWidth
            size="small"
            variant="outlined"
            value={visionModel}
            onChange={(e) => setVisionModel(e.target.value)}
          />
        </FormField>
        <FormField full label={i18n.t("aiUsage.platform.quotaMessage")} htmlFor="ai-platform-quota">
          <TextField
            id="ai-platform-quota"
            fullWidth
            size="small"
            variant="outlined"
            multiline
            minRows={2}
            value={quotaMessage}
            onChange={(e) => setQuotaMessage(e.target.value)}
          />
        </FormField>
      </FormGrid>
      <div className={classes.formFooter}>
        <ButtonWithSpinner
          variant="contained"
          color="primary"
          startIcon={<SaveOutlinedIcon />}
          loading={saving}
          onClick={handleSave}
        >
          {i18n.t("aiUsage.platform.save")}
        </ButtonWithSpinner>
      </div>
    </Section>
  );
};

/* ---------------- Tabela de preços ---------------- */

const PricesSection = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const [prices, setPrices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/ai-usage/prices");
        if (active) setPrices(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const updateRow = (idx, patch) =>
    setPrices((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleAdd = () => {
    const model = window.prompt(i18n.t("aiUsage.platform.newModelPrompt"));
    const name = (model || "").trim();
    if (!name) return;
    if (prices.some((p) => p.model === name)) return;
    setPrices((rows) => [
      ...rows,
      { model: name, inputPerMillionUsd: 0, outputPerMillionUsd: 0, audioPerMinuteUsd: 0, ttsPerMillionCharsUsd: 0, active: true },
    ]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = prices.map((p) => ({
        model: p.model,
        inputPerMillionUsd: toNumber(p.inputPerMillionUsd),
        outputPerMillionUsd: toNumber(p.outputPerMillionUsd),
        audioPerMinuteUsd: toNumber(p.audioPerMinuteUsd),
        ttsPerMillionCharsUsd: toNumber(p.ttsPerMillionCharsUsd),
        active: p.active !== false,
      }));
      const { data } = await api.put("/ai-usage/prices", { prices: payload });
      if (Array.isArray(data)) setPrices(data);
      toast.success(i18n.t("aiUsage.platform.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      flush
      className={classes.block}
      title={i18n.t("aiUsage.platform.prices")}
      actions={
        <>
          <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleAdd}>
            {i18n.t("aiUsage.platform.addModel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            color="primary"
            startIcon={<SaveOutlinedIcon />}
            loading={saving}
            disabled={saving || prices.length === 0}
            onClick={handleSave}
          >
            {i18n.t("aiUsage.platform.save")}
          </ButtonWithSpinner>
        </>
      }
    >
      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("aiUsage.platform.model")}</TableCell>
              {PRICE_COLUMNS.map((c) => (
                <TableCell key={c.key} align="right">
                  {i18n.t(`aiUsage.platform.${c.label}`)}
                </TableCell>
              ))}
              <TableCell align="center">{i18n.t("aiUsage.platform.active")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.length === 0 && (
              <TableRow>
                <TableCell colSpan={PRICE_COLUMNS.length + 2} className={panel.empty}>
                  Nenhum modelo cadastrado. Use "{i18n.t("aiUsage.platform.addModel")}" para começar.
                </TableCell>
              </TableRow>
            )}
            {prices.map((p, idx) => (
              <TableRow key={p.model}>
                <TableCell className={panel.cellStrong}>{p.model}</TableCell>
                {PRICE_COLUMNS.map((c) => (
                  <TableCell key={c.key} align="right">
                    <TextField
                      className={classes.cellField}
                      size="small"
                      variant="outlined"
                      type="number"
                      inputProps={{
                        step: "0.0001",
                        min: 0,
                        "aria-label": `${i18n.t(`aiUsage.platform.${c.label}`)} — ${p.model}`,
                      }}
                      value={p[c.key] === null || p[c.key] === undefined ? "" : p[c.key]}
                      onChange={(e) => updateRow(idx, { [c.key]: e.target.value })}
                    />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Switch
                    size="small"
                    color="primary"
                    checked={p.active !== false}
                    onChange={(e) => updateRow(idx, { active: e.target.checked })}
                    inputProps={{ "aria-label": `${i18n.t("aiUsage.platform.active")} — ${p.model}` }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Section>
  );
};

/* ---------------- Consumo por empresa ---------------- */

const CompaniesSection = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const [period, setPeriod] = useState(currentPeriod());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-usage/companies", { params: { period: p } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(period)) fetchRows(period);
  }, [period, fetchRows]);

  const handleExtraCredit = async (row) => {
    const raw = window.prompt(i18n.t("aiUsage.platform.extraCreditPrompt"), "0");
    if (raw === null) return;
    const amount = toNumber(raw, NaN);
    if (!Number.isFinite(amount) || amount < 0) return;
    setBusyId(row.companyId);
    try {
      await api.post(`/ai-usage/companies/${row.companyId}/extra-credit`, { amount });
      toast.success(i18n.t("aiUsage.platform.extraCreditSaved"));
      await fetchRows(period);
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleEnforce = async (row, enforce) => {
    setBusyId(row.companyId);
    try {
      const { data } = await api.put(`/ai-usage/companies/${row.companyId}/enforce`, { enforce });
      setRows((list) =>
        list.map((r) => (r.companyId === row.companyId ? { ...r, enforce: !!data?.aiEnforce } : r))
      );
      toast.success(i18n.t("aiUsage.platform.enforceSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleClose = async () => {
    if (!window.confirm(i18n.t("aiUsage.platform.confirmClose", { period }))) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/ai-usage/close?period=${encodeURIComponent(period)}`);
      toast.success(
        i18n.t("aiUsage.platform.closed", {
          n: Number(data?.statements) || 0,
          v: fmt(data?.overageTotal),
        })
      );
      await fetchRows(period);
    } catch (err) {
      toastError(err);
    } finally {
      setClosing(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      cost: acc.cost + (Number(r.costBrl) || 0),
      charged: acc.charged + (Number(r.chargedBrl) || 0),
      profit: acc.profit + (Number(r.profitBrl) || 0),
    }),
    { cost: 0, charged: 0, profit: 0 }
  );

  return (
    <Section
      flush
      className={classes.block}
      title={i18n.t("aiUsage.platform.companies")}
      actions={
        <>
          <TextField
            className={classes.periodField}
            size="small"
            variant="outlined"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            inputProps={{ "aria-label": i18n.t("aiUsage.platform.period") }}
          />
          <ButtonWithSpinner
            variant="outlined"
            startIcon={<LockOutlinedIcon />}
            loading={closing}
            disabled={closing || loading}
            onClick={handleClose}
          >
            {i18n.t("aiUsage.platform.close")}
          </ButtonWithSpinner>
        </>
      }
    >
      {rows.length > 0 && (
        <div className={classes.kpis}>
          <StatCard tone="brand" label={i18n.t("aiUsage.platform.charged")} value={brl(totals.charged)} />
          <StatCard tone="neutral" label={i18n.t("aiUsage.platform.cost")} value={brl(totals.cost)} />
          <StatCard
            tone={totals.profit >= 0 ? "success" : "error"}
            label={i18n.t("aiUsage.platform.profit")}
            value={brl(totals.profit)}
          />
        </div>
      )}

      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("aiUsage.platform.company")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.used")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.cost")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.charged")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.profit")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.credit")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.cap")}</TableCell>
              <TableCell align="center">{i18n.t("aiUsage.platform.enforce")}</TableCell>
              <TableCell align="right">{i18n.t("aiUsage.platform.extraCredit")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={9} />}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className={panel.empty}>
                  {i18n.t("aiUsage.platform.noCompanies")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((r) => {
                const used = Number(r.usedBrl) || 0;
                const cap = Number(r.capBrl) || 0;
                const profit = Number(r.profitBrl) || 0;
                const busy = busyId === r.companyId;
                return (
                  <TableRow key={r.companyId}>
                    <TableCell className={panel.cellStrong}>{r.name}</TableCell>
                    <TableCell align="right" className={cap > 0 && used >= cap ? classes.over : undefined}>
                      {brl(used)}
                    </TableCell>
                    <TableCell align="right">{brl(r.costBrl)}</TableCell>
                    <TableCell align="right">{brl(r.chargedBrl)}</TableCell>
                    <TableCell align="right" className={profit >= 0 ? classes.positive : classes.negative}>
                      {brl(profit)}
                    </TableCell>
                    <TableCell align="right">{brl(r.creditBrl)}</TableCell>
                    <TableCell align="right">{brl(cap)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={r.enforce ? i18n.t("aiUsage.platform.enforceOn") : i18n.t("aiUsage.platform.enforceOff")}>
                        <Switch
                          size="small"
                          color="primary"
                          checked={!!r.enforce}
                          disabled={busy}
                          onChange={(e) => handleEnforce(r, e.target.checked)}
                          inputProps={{ "aria-label": `${i18n.t("aiUsage.platform.enforce")} — ${r.name}` }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <div className={panel.rowActions}>
                        <Tooltip title={i18n.t("aiUsage.platform.extraCredit")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => handleExtraCredit(r)}
                              aria-label={i18n.t("aiUsage.platform.extraCredit")}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
    </Section>
  );
};

/* ---------------- App Meta (Embedded Signup da API oficial) ---------------- */

const MetaAppSection = () => {
  const classes = useStyles();
  const [view, setView] = useState({ appId: "", configId: "", hasSecret: false, fromEnv: {} });
  const [appId, setAppId] = useState("");
  const [configId, setConfigId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setView({ appId: data?.appId || "", configId: data?.configId || "", hasSecret: !!data?.hasSecret, fromEnv: data?.fromEnv || {} });
    setAppId(data?.appId || "");
    setConfigId(data?.configId || "");
    setAppSecret("");
  };

  useEffect(() => {
    api.get("/meta-signup/platform").then(({ data }) => apply(data)).catch((err) => toastError(err));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (appId !== view.appId) payload.appId = appId;
      if (configId !== view.configId) payload.configId = configId;
      if (appSecret.trim()) payload.appSecret = appSecret.trim();
      const { data } = await api.put("/meta-signup/platform", payload);
      apply(data);
      toast.success(i18n.t("aiUsage.platform.metaApp.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const envChip = (key) =>
    view.fromEnv?.[key] ? (
      <InputAdornment position="end">
        <StatusBadge color="neutral" size="sm" dot={false} label={i18n.t("aiUsage.platform.metaApp.fromEnv")} />
      </InputAdornment>
    ) : null;

  return (
    <Section
      className={classes.block}
      title={i18n.t("aiUsage.platform.metaApp.title")}
      actions={
        view.hasSecret && (
          <StatusBadge
            color="success"
            icon={<LockOutlinedIcon style={{ fontSize: 13 }} />}
            label={i18n.t("aiUsage.platform.metaApp.secretSet")}
          />
        )
      }
    >
      <p className={classes.lead}>{i18n.t("aiUsage.platform.metaApp.help")}</p>
      <FormGrid columns={3}>
        <FormField label={i18n.t("aiUsage.platform.metaApp.appId")} htmlFor="meta-app-id">
          <TextField
            id="meta-app-id"
            fullWidth
            size="small"
            variant="outlined"
            value={appId}
            onChange={(e) => setAppId(e.target.value.replace(/\D/g, ""))}
            InputProps={{ endAdornment: envChip("appId") }}
          />
        </FormField>
        <FormField label={i18n.t("aiUsage.platform.metaApp.configId")} htmlFor="meta-config-id">
          <TextField
            id="meta-config-id"
            fullWidth
            size="small"
            variant="outlined"
            value={configId}
            onChange={(e) => setConfigId(e.target.value.replace(/\D/g, ""))}
            InputProps={{ endAdornment: envChip("configId") }}
          />
        </FormField>
        <FormField label={i18n.t("aiUsage.platform.metaApp.secret")} htmlFor="meta-app-secret">
          <TextField
            id="meta-app-secret"
            fullWidth
            size="small"
            variant="outlined"
            type="password"
            autoComplete="new-password"
            placeholder={view.hasSecret ? i18n.t("aiUsage.platform.metaApp.secretSet") : ""}
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            InputProps={{ endAdornment: envChip("appSecret") }}
          />
        </FormField>
      </FormGrid>
      <div className={classes.formFooter}>
        <ButtonWithSpinner variant="contained" color="primary" loading={saving} onClick={save}>
          {i18n.t("aiUsage.platform.metaApp.save")}
        </ButtonWithSpinner>
      </div>
    </Section>
  );
};

/* ---------------- Página ---------------- */

const AiPlatform = () => {
  const classes = useStyles();
  return (
    <MainContainer maxWidth>
      <PageHeader
        title={i18n.t("aiUsage.platform.title")}
        subtitle={i18n.t("aiUsage.platform.subtitle")}
      />
      <div className={classes.scroll}>
        <SettingsSection />
        <MetaAppSection />
        <PricesSection />
        <CompaniesSection />
      </div>
    </MainContainer>
  );
};

export default AiPlatform;
